import React from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'

import { Button, Card, HeroCard } from '@acorns/web-components'
import styled from 'styled-components'

import InfoIconSVG from 'assets/icons/info.svg'
import { withTrackClicked } from 'components/trackers'
import { Bodytext, Heading } from 'components/typography'
import { BankAccount, Profile, User } from 'generated/types'
import { theme } from 'theme'

export type GqlProps = {
  loading: boolean
  user: User
  profile: Profile
  spendAccounts: BankAccount[]
}

export const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`

export const CenteredContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`

export const DirectDepositCard = styled(Card)`
  margin-bottom: 20px;
  padding: 0;

  &:last-child {
    margin-bottom: 0;
  }
`

export const Divider = styled.div`
  background: ${theme('colors.ivory')};
  height: 1px;
  width: 100%;
`

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding: 30px 50px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    flex-direction: column;
    padding: 20px;
  }
`

export const StyledButton = styled(Button)`
  &&& {
    margin-right: 15px;
    min-width: 150px;
    border-color: inherit;

    @media screen and (max-width: ${theme('breakpoints.md')}px) {
      margin-bottom: 20px;
      margin-right: 0;
      padding-right: 20px;
      width: 100%;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`
export const EditButton = styled(Button)`
  &&& {
    position: absolute;
    right: 50px;
    top: 45px;
    border-color: inherit;

    @media screen and (max-width: ${theme('breakpoints.md')}px) {
      right: 20px;
      top: 24px;
    }
  }
`

export const ExternalButtonLink = styled.a`
  background-color: ${theme('colors.green')};
  border-radius: 100px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  height: 44px;
  line-height: 22px;
  padding: 11px 25px;
  text-decoration: none;
  text-align: center;
  min-width: 150px;
  cursor: pointer;

  &.disabled {
    background: ${theme('colors.stoneLight')};
    border-color: transparent;
    color: white;
    cursor: default;
  }
`

export const FieldsContainer = styled.div`
  padding: 50px 50px 20px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    padding: 24px 20px 0 20px;
  }
`

export const StyledHeading = styled(Heading)`
  margin-bottom: 40px;
`

export const FieldRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  padding-bottom: 20px;
  padding-top: 10px;
  margin-left: -10px;
  margin-right: -10px;

  &:first-child {
    padding-top: 20px;
  }

  &:last-child {
    padding-bottom: 0;
  }

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    padding-bottom: 0;
  }
`

export const Field = styled<{ width?: string }, any>('div')`
  display: block;
  flex-basis: ${(p) => (p.width ? p.width : '50%')};
  max-width: ${(p) => (p.width ? p.width : '50%')};
  padding-left: 10px;
  padding-right: 10px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    flex-basis: 100%;
    max-width: 100%;
    padding-bottom: 20px;
  }
`

export const FieldLabel = styled.label`
  color: ${theme('colors.slate')};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
`

export const FieldName = styled.div`
  color: ${theme('colors.slate')};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
`

export const FieldValue = styled.span`
  color: ${theme('colors.slate')};
  display: block;
  font-size: 14px;
  line-height: 2.5;
`

export const FieldTip = styled(FieldValue)`
  color: ${theme('colors.stone')};
  line-height: 1.43;
  display: inline-flex;
  margin-left: 10px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    display: block;
    margin-left: 0;
  }
`

export const GreenText = styled.span`
  color: #51cc26;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.36;
`

const FakeInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 46px;
  border-radius: ${theme('styles.borderRadius')};
  border: 1px solid ${theme('colors.ivory')};
  margin-top: 6px;
  padding: 13px 0 13px 16px;
  box-sizing: border-box;
`

const CopyButton = styled(Button)`
  &&& {
    color: ${theme('colors.slate')};
    font-weight: normal;
    border-top-right-radius: 9px;
    border-bottom-right-radius: 9px;
  }
`

const TrackedCopyButton = withTrackClicked<any, any>(() => ({
  object_name: 'directDepositOnlineCopy',
  screen: 'directDepositOnline',
  screen_name: 'directDepositOnline',
}))(CopyButton)

export const CopyInput: React.SFC<{ text: string }> = ({ text }) => (
  <FakeInput>
    <Bodytext>{text}</Bodytext>

    <CopyToClipboard text={text}>
      <TrackedCopyButton type="flat" secondary={true} onPress={null}>
        Copy
      </TrackedCopyButton>
    </CopyToClipboard>
  </FakeInput>
)

const InfoIconContainer = styled.div`
  width: 24px;
  height: 24px;
  background-color: ${theme('colors.ivory')};
  border-radius: 50%;
  margin-right: 10px;
`

const InfoImg = styled.img`
  width: 24px;
  height: 24px;
  padding: 3px;
`

const EmployerInfoContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    justify-content: center;
    padding: 20px 0;
    margin-top: 20px;
  }
`

const EmployerText = styled(Bodytext)`
  color: ${theme('colors.stone')};
`

export const EmployerInfo = () => (
  <EmployerInfoContainer>
    <InfoIconContainer>
      <InfoImg src={InfoIconSVG} />
    </InfoIconContainer>
    <EmployerText>
      Log into your employer’s portal to update your direct deposit.
    </EmployerText>
  </EmployerInfoContainer>
)

export const StyledHero = styled(HeroCard)`
  height: 300px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    height: 231px;
  }
`

export const StyledHeroContainer = styled.div`
  height: 300px;
  margin-bottom: 20px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    height: 231px;
  }

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    padding: 0 30px;
    margin-bottom: 0px;
  }
`

export const HeroTitle = styled(Heading)`
  color: white;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  padding-bottom: 5px;
`

export const HeroText = styled(Bodytext)`
  color: ${theme('colors.stoneLight')};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  text-align: center;
  max-width: 500px;
`
